import { useEffect, useRef, useState } from 'react';
import { useReducedMotion } from '@/lib/useReducedMotion';

interface RingProps {
  /** Bound panels. */
  value: number;
  /** Total panels. */
  total: number;
  size?: number;
  label?: string;
}

/**
 * Coverage dial: an arc that sweeps to the real bound/total ratio when it
 * scrolls into view.
 *
 * Unlike the decorative motion elsewhere, this one does encode a value — the
 * count of panels with a connector — so the number is printed in the middle
 * rather than left to the arc, and it is announced to assistive tech.
 */
export function Ring({ value, total, size = 84, label }: RingProps) {
  const ref = useRef<HTMLDivElement>(null);
  const reduced = useReducedMotion();
  const target = total > 0 ? value / total : 0;
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    if (reduced || !('IntersectionObserver' in window)) {
      setProgress(target);
      return;
    }

    let raf = 0;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          observer.unobserve(entry.target);

          const start = performance.now();
          const step = (now: number) => {
            const t = Math.min(1, (now - start) / 1100);
            // easeOutExpo — commits quickly, settles slowly.
            setProgress(target * (t === 1 ? 1 : 1 - Math.pow(2, -10 * t)));
            if (t < 1) raf = requestAnimationFrame(step);
          };
          raf = requestAnimationFrame(step);
        }
      },
      { threshold: 0.5 }
    );

    observer.observe(el);
    return () => {
      cancelAnimationFrame(raf);
      observer.disconnect();
    };
  }, [target, reduced]);

  const pct = Math.round(progress * 100);

  return (
    <div
      ref={ref}
      className="ring"
      style={{ width: size, height: size, ['--p' as string]: `${pct}%` }}
      role="img"
      aria-label={label ?? `${value} of ${total} panels bound`}
    >
      <span className="ring__value" aria-hidden="true">
        <strong>{value}</strong>
        <em>/{total}</em>
      </span>
    </div>
  );
}
